import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { apiSlice } from "../../app/apiSlice";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { addToast } from "../toast/toast.slice";
import {
	inferUploadContentType,
	toSignedUrlFileName,
} from "./inferUploadContentType";
import { putFileToS3, S3PutError } from "./putFileToS3";
import { parseSignedUrlResponse } from "./upload.parsers";

export interface UploadMediaResponse {
	bucket: string;
	key: string;
	contentType: string;
	url: string;
}

export interface UploadMediaArgs {
	file: File;
	/** S3-style prefix, e.g. `events` or `property-marketing` */
	folder: string;
	onProgress?: (percent: number) => void;
}

export {
	parseSignedUrlResponse,
	parseUploadMediaResponse,
	resolveUploadStorageKey,
} from "./upload.parsers";

export const FOLDER_EVENTS = "events";
export const FOLDER_PROPERTY_MARKETING = "property-marketing";
export const FOLDER_PROPERTIES = "properties";
export const FOLDER_FNB = "fnb-restaurants";
export const FOLDER_CABANA = "cabanas";
export const FOLDER_STATIC = "static";
export const FOLDER_OTHERS = "others";
export const FOLDER_CHARGES_INVOICE_HEADER = "charges/invoice-header";
export const FOLDER_CHARGES_INVOICE_FOOTER = "charges/invoice-footer";
export const FOLDER_CHARGES_INVOICES = "charges/invoices";
export const FOLDER_REVENUE_STATEMENT_HEADER = "revenue/statement-header";
export const FOLDER_REVENUE_STATEMENTS = "revenue/statements";
export const FOLDER_MONTHLY_STATEMENT_HEADER = "monthly-statement/header";
export const FOLDER_MONTHLY_STATEMENTS = "monthly-statement/statements";

const UNSUPPORTED_TYPE_MESSAGE = "This file type is not supported for upload.";
const MISSING_SIGNED_URL_MESSAGE =
	"Upload could not be started. Please try again.";

function toQueryError(status: number, message: string): FetchBaseQueryError {
	return { status, data: { message } };
}

export const uploadApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		uploadMedia: builder.mutation<UploadMediaResponse, UploadMediaArgs>({
			async queryFn({ file, folder, onProgress }, api, _extra, baseQuery) {
				const contentType = inferUploadContentType(file);
				if (!contentType) {
					api.dispatch(
						addToast({ type: "error", message: UNSUPPORTED_TYPE_MESSAGE }),
					);
					return { error: toQueryError(400, UNSUPPORTED_TYPE_MESSAGE) };
				}

				const presign = await baseQuery({
					url: API_ENDPOINTS.upload.signedUrl,
					method: "POST",
					body: {
						contentType,
						folder,
						fileName: toSignedUrlFileName(file),
					},
				});
				if (presign.error) return { error: presign.error };

				const signed = parseSignedUrlResponse(presign.data);
				if (!signed.key || !signed.uploadUrl) {
					api.dispatch(
						addToast({ type: "error", message: MISSING_SIGNED_URL_MESSAGE }),
					);
					return { error: toQueryError(502, MISSING_SIGNED_URL_MESSAGE) };
				}

				try {
					await putFileToS3({
						uploadUrl: signed.uploadUrl,
						file,
						contentType,
						onProgress,
					});
				} catch (cause) {
					const message =
						cause instanceof S3PutError
							? cause.message
							: "Could not upload the file. Please try again.";
					const status = cause instanceof S3PutError ? cause.status : 0;
					api.dispatch(addToast({ type: "error", message }));
					return {
						error:
							status === 0
								? { status: "FETCH_ERROR", error: message }
								: toQueryError(status, message),
					};
				}

				return {
					data: {
						bucket: signed.bucket,
						key: signed.key,
						contentType: signed.contentType || contentType,
						url: signed.url,
					},
				};
			},
		}),
	}),
	overrideExisting: false,
});

export const { useUploadMediaMutation } = uploadApi;
